import Vue from 'vue';
import buildApiQuery from '@/lib/buildApiQuery';
import handleErrors from '@/lib/handleErrors';
import isEqual from 'lodash/isEqual';
import kmToMiles from '@/lib/kmToMiles';
import nonreactive from '@/lib/nonreactive';

const moduleState = {
  geojson: {
    type: 'FeatureCollection',
    features: [],
  },
  searchLocationsGeojson: {
    type: 'FeatureCollection',
    features: [],
  },
  nearestCenter: null,
  currentApiQuery: {},
  analyzeMapError: null,
};

const moduleGetters = {
  analyzeStationsGeojson: (state) => state.geojson,
  analyzeStationsSearchLocationsGeojson: (state) => state.searchLocationsGeojson,
  analyzeNearestCenter: (state) => state.nearestCenter,
  getAnalyzeMapError: (state) => state.analyzeMapError,
};

const mutations = {
  saveAnalyzeMapCurrentApiQuery(state, apiQuery) {
    state.currentApiQuery = apiQuery;
  },

  saveAnalyzeMapStations(state, payload) {
    const geojson = {
      type: 'FeatureCollection',
      features: [],
    };

    const searchLocationsGeojson = {
      type: 'FeatureCollection',
      features: [],
    };

    state.nearestCenter = null;

    if (payload && payload.data && payload.data.fuel_stations) {
      for (let i = 0, len = payload.data.fuel_stations.length; i < len; i += 1) {
        const station = payload.data.fuel_stations[i];
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [station.longitude, station.latitude],
          },
          properties: {
            id: station.id,
            fuel: station.fuel_type_code,
          },
        });
      }

      if (payload.data.latitude && payload.data.longitude) {
        searchLocationsGeojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [payload.data.longitude, payload.data.latitude],
          },
          properties: {
            color: 'red',
          },
        });

        state.nearestCenter = [payload.data.longitude, payload.data.latitude];
      }
    }

    state.geojson = nonreactive.frozen(geojson);
    state.searchLocationsGeojson = nonreactive.frozen(searchLocationsGeojson);

    if (payload) {
      state.currentApiQuery = payload.apiQuery;
    }
  },

  setAnalyzeMapError(state, status) {
    state.analyzeMapError = status;
  },
};

const actions = {
  async fetchAnalyzeMapStations({ commit, state, getters }, query) {
    try {
      const apiQuery = {
        ...buildApiQuery(getters, query),
        response_format: 'compact',
      };

      // The exact API used depends on whether we're doing an address-based
      // radius filter or a region-based contains filter.
      let apiUrl;
      if (query.location_mode === 'address') {
        apiQuery.location = query.location;
        apiQuery.radius = query.radius;
        apiQuery.limit = 'all';
        apiUrl = `${process.env.VUE_APP_API_URL_ROOT}/v1/nearest.json`;

        if (getters.getFilterValueWithDefault('radius_unit') === 'km') {
          apiQuery.radius = kmToMiles(apiQuery.radius);
        }
      } else {
        apiQuery.state = query.region;
        apiUrl = `${process.env.VUE_APP_API_URL_ROOT}/v1.json`;
      }

      if (isEqual(apiQuery, state.currentApiQuery)) {
        return;
      }
      commit('setLoadingAnalyzeStations', true);
      commit('saveAnalyzeMapCurrentApiQuery', apiQuery);

      // If the start or end location is empty, then return an empty result without
      // actually performing an ajax query.
      if (query.location_mode === 'address' && (apiQuery.location === undefined || apiQuery.location === null || apiQuery.location === '')) {
        commit('saveAnalyzeStations', {
          apiQuery,
        });
        commit('setLoadingAnalyzeStations', false);
        return;
      }

      const response = await Vue.axios.get(apiUrl, {
        params: apiQuery,
      });
      commit('saveAnalyzeMapStations', {
        apiQuery,
        data: response.data,
      });
      commit('setAnalyzeMapError', null);
    } catch (e) {
      commit('saveAnalyzeMapStations');
      commit('setAnalyzeMapError', handleErrors(e));
    } finally {
      commit('setLoadingAnalyzeStations', false);
    }
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
